import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { SimpleIconPicker } from "@/components/ui/simple-icon-picker";
import { Switch } from "@/components/ui/switch";
import { Plus, X, Star, Sparkles, Video, FileText, Award, Podcast, ImageIcon } from "lucide-react";
import type { ProgramForm as ProgramFormType } from "@/types/program";
import { DeliveryModeV2 } from "@/types/program-v2";
import { ProgramTypeHeroImageUpload } from "../ProgramTypeHeroImageUpload";

interface ContentStepV2Props {
  data: Partial<ProgramFormType>;
  onUpdate: (data: Partial<ProgramFormType>) => void;
  programType?: string;
  formData?: any;
  isEditMode?: boolean;
  onFieldChange?: (fieldPath: string) => void;
  activeProgramType?: DeliveryModeV2;
  programTypes?: Record<string, any>;
}

interface KeyFeature {
  id: number;
  name: string;
  icon: string;
  value: string;
  description: string;
}

export const ContentStepV2: React.FC<ContentStepV2Props> = ({
  data,
  onUpdate,
  onFieldChange,
  activeProgramType,
}) => {
  const keyFeatures = data.keyFeatures || [];
  const [showCommonFeatures, setShowCommonFeatures] = React.useState(false);
  
  // Type-specific fields - accessed directly from data (which is ProgramTypeDataV2)
  const subtitle = (data as any)?.subtitle || '';
  const about = (data as any)?.about || '';
  const imageUrls = (data as any)?.imageUrls ?? [];
  const videoUrl = (data as any)?.videoUrl || '';
  const nationalCertifications = (data as any)?.nationalCertifications || { isallIncluded: false, items: [] };
  const podcastData = (data as any)?.podcastData || [];
  const thisCourseIncludesData = (data as any)?.thisCourseIncludesData || [];
  const programComparison = (data as any)?.programComparison || [];

  // Common key features for quick addition
  const COMMON_KEY_FEATURES = [
    {
      name: "Duration",
      icon: "clock",
      value: "4 - 6 Weeks",
      description: "Complete your program in just 4-6 weeks with our accelerated curriculum."
    },
    {
      name: "Start Date",
      icon: "calendar",
      value: "1st & 15th",
      description: "New classes start on the 1st and 15th of every month for your convenience."
    },
    {
      name: "Delivery",
      icon: "map-pin",
      value: "Hybrid", // This will be automatically updated based on program type
      description: "Combines online theory with hands-on clinical practice for the best learning experience."
    },
    {
      name: "Program Pace",
      icon: "users",
      value: "Self Pace",
      description: "Learn at your own pace with flexible scheduling that fits your lifestyle."
    },
    {
      name: "Time Commitment",
      icon: "clock",
      value: "2-3 Hours/day",
      description: "Dedicate just 2-3 hours per day to complete your program successfully."
    },
    {
      name: "Language",
      icon: "book-open",
      value: "English",
      description: "All course materials and instruction are provided in English."
    },
    {
      name: "Payment",
      icon: "credit-card",
      value: "Starting $99",
      description: "Start your career journey with affordable payment plans starting at just $99."
    },
    {
      name: "Certification",
      icon: "award",
      value: "Included",
      description: "Earn industry-recognized certifications upon successful completion of your program."
    }
  ];

  // Key features management
  const addKeyFeature = () => {
    const newFeature: KeyFeature = {
      id: Date.now(),
      name: "",
      icon: "star",
      value: "",
      description: "",
    };
    onUpdate({ keyFeatures: [...keyFeatures, newFeature] });
    onFieldChange?.('keyFeatures');
  };

  const addCommonKeyFeature = (commonFeature: typeof COMMON_KEY_FEATURES[0]) => {
    const newFeature: KeyFeature = {
      id: Date.now(),
      name: commonFeature.name,
      icon: commonFeature.icon,
      value: commonFeature.value,
      description: commonFeature.description,
    };
    onUpdate({ keyFeatures: [...keyFeatures, newFeature] });
    onFieldChange?.('keyFeatures');
  };

  const updateKeyFeature = (index: number, updatedFeature: KeyFeature) => {
    const updatedFeatures = [...keyFeatures];
    updatedFeatures[index] = updatedFeature;
    onUpdate({ keyFeatures: updatedFeatures });
    onFieldChange?.('keyFeatures');
  };

  const removeKeyFeature = (index: number) => {
    const updatedFeatures = keyFeatures.filter((_, i) => i !== index);
    onUpdate({ keyFeatures: updatedFeatures });
    onFieldChange?.('keyFeatures');
  };

  // Handlers for type-specific fields
  const updateTypeSpecificField = (field: string, value: any) => {
    onUpdate({ [field]: value });
    onFieldChange?.(field);
  };

  // National Certifications handlers
  const updateNationalCertification = (field: string, value: any) => {
    const updatedCerts = {
      ...nationalCertifications,
      [field]: value
    };
    updateTypeSpecificField('nationalCertifications', updatedCerts);
  };

  const addCertificationItem = () => {
    const updatedItems = [...(nationalCertifications.items || []), ''];
    updateNationalCertification('items', updatedItems);
  };

  const updateCertificationItem = (index: number, value: string) => {
    const updatedItems = [...(nationalCertifications.items || [])];
    updatedItems[index] = value;
    updateNationalCertification('items', updatedItems);
  };

  const removeCertificationItem = (index: number) => {
    const updatedItems = (nationalCertifications.items || []).filter((_: any, i: number) => i !== index);
    updateNationalCertification('items', updatedItems);
  };

  // Podcast Data handlers
  const addPodcast = () => {
    const newPodcast = {
      id: `pod-${Date.now()}`,
      title: '',
      category: '',
      duration: '',
      imageUrl: '',
      audioUrl: ''
    };
    updateTypeSpecificField('podcastData', [...podcastData, newPodcast]);
  };

  const updatePodcast = (index: number, field: string, value: string) => {
    const updatedPodcasts = [...podcastData];
    updatedPodcasts[index] = { ...updatedPodcasts[index], [field]: value };
    updateTypeSpecificField('podcastData', updatedPodcasts);
  };

  const removePodcast = (index: number) => {
    const updatedPodcasts = podcastData.filter((_: any, i: number) => i !== index);
    updateTypeSpecificField('podcastData', updatedPodcasts);
  };

  return (
    <div className="space-y-8">
      {/* SECTION 0: Basic Content Fields (Per Program Type) */}
      <div className="space-y-6">
        {/* Info Banner */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-blue-900 text-sm">Type-Specific Content</h4>
              <p className="text-sm text-blue-700 mt-1">
                These fields are unique to <strong>{activeProgramType}</strong> program type. 
                Each delivery method can have its own subtitle, description, video, certifications, and podcasts.
              </p>
            </div>
          </div>
        </div>

        {/* Subtitle */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">Subtitle *</Label>
            </div>
            <Input
              value={subtitle}
              onChange={(e) => updateTypeSpecificField('subtitle', e.target.value)}
              placeholder={`E.g., "8-week ${activeProgramType} program with hands-on training"`}
              className="w-full"
            />
            <p className="text-sm text-gray-500">
              A short description that appears below the program title
            </p>
          </div>
        </Card>

        {/* About / Description */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">Description (About) *</Label>
            </div>
            <div className="border border-input rounded-md p-1 min-h-[250px] bg-muted/30">
              <RichTextEditor
                value={about}
                onChange={(value) => updateTypeSpecificField('about', value)}
                minHeight={250}
                maxHeight={800}
                showBorder={true}
                enableAI={true}
                title="Description (About)"
                aiContext={`Program type: ${activeProgramType}`}
              />
            </div>
            <p className="text-sm text-gray-500">
              Detailed description specific to this delivery method
            </p>
          </div>
        </Card>

        {/* Hero images */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-gray-600" />
              <span className="text-base font-semibold text-gray-900">Hero images</span>
            </div>
            <ProgramTypeHeroImageUpload
              imageUrls={imageUrls}
              onChange={(urls) => updateTypeSpecificField('imageUrls', urls)}
            />
            <p className="text-sm text-gray-500">
              Optional hero images for this program type (e.g. header carousel)
            </p>
          </div>
        </Card>

        {/* Video URL */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Video className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">Video URL</Label>
            </div>
            <Input
              value={videoUrl}
              onChange={(e) => updateTypeSpecificField('videoUrl', e.target.value)}
              placeholder="https://example.com/video.mp4"
              className="w-full"
            />
            <p className="text-sm text-gray-500">
              Introduction or promotional video for this specific program type
            </p>
          </div>
        </Card>

        {/* National Certifications */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">National Certifications</Label>
            </div>
            
            <div className="flex items-center space-x-3 space-y-2">
              <Switch
                id={`all-included-${activeProgramType}`}
                checked={nationalCertifications?.isallIncluded || false}
                onCheckedChange={(checked) => updateNationalCertification('isallIncluded', checked)}
              />
              <Label htmlFor={`all-included-${activeProgramType}`} className="text-sm font-medium">
                All certifications included in program
              </Label>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Certification Items
              </Label>
              {(nationalCertifications?.items || []).map((item: string, index: number) => {
                // Check if item has the "*" suffix
                const isIncluded = item.endsWith('*');
                const displayValue = isIncluded ? item.slice(0, -1) : item; // Hide * from user

                return (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={displayValue}
                      onChange={(e) => {
                        const baseValue = e.target.value;
                        const newValue = isIncluded ? baseValue + '*' : baseValue;
                        updateCertificationItem(index, newValue);
                      }}
                      placeholder="e.g., NHA CCMA Certification, BLS Certification"
                      className="flex-1"
                    />
                    <div className="flex items-center space-x-3 space-y-2 min-w-[120px]">
                      <Switch
                        id={`cert-included-${activeProgramType}-${index}`}
                        checked={isIncluded}
                        onCheckedChange={(checked) => {
                          let newValue = item.endsWith('*') ? item.slice(0, -1) : item; // Remove * if exists
                          if (checked) {
                            newValue = newValue + '*'; // Add *
                          }
                          updateCertificationItem(index, newValue);
                        }}
                      />
                      <Label htmlFor={`cert-included-${activeProgramType}-${index}`} className="text-xs font-medium whitespace-nowrap">
                        Included
                      </Label>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCertificationItem(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={addCertificationItem}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Certification
              </Button>
            </div>
          </div>
        </Card>

        {/* Podcast Data */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Podcast className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">Podcast Data</Label>
            </div>
            
            {podcastData.map((item: any, index: number) => (
              <Card key={index} className="p-4 bg-gray-50">
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Podcast Title</Label>
                      <Input
                        value={item.title}
                        onChange={(e) => updatePodcast(index, 'title', e.target.value)}
                        placeholder="Podcast title"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Category</Label>
                      <Input
                        value={item.category}
                        onChange={(e) => updatePodcast(index, 'category', e.target.value)}
                        placeholder="Category"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Duration</Label>
                      <Input
                        value={item.duration}
                        onChange={(e) => updatePodcast(index, 'duration', e.target.value)}
                        placeholder="e.g., 30 min"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Image URL</Label>
                      <Input
                        value={item.imageUrl}
                        onChange={(e) => updatePodcast(index, 'imageUrl', e.target.value)}
                        placeholder="Image URL"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Audio URL</Label>
                    <Input
                      value={item.audioUrl}
                      onChange={(e) => updatePodcast(index, 'audioUrl', e.target.value)}
                      placeholder="Audio URL"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removePodcast(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove Podcast
                  </Button>
                </div>
              </Card>
            ))}

            <Button
              variant="outline"
              onClick={addPodcast}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Podcast
            </Button>
          </div>
        </Card>

        {/* This Course Includes */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">This Course Includes</Label>
            </div>
            <p className="text-sm text-gray-500">
              Add features and materials included in this specific program type
            </p>
            
            {thisCourseIncludesData.map((item: any, index: number) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={item.name}
                  onChange={(e) => {
                    const updated = [...thisCourseIncludesData];
                    updated[index] = { ...item, name: e.target.value };
                    updateTypeSpecificField('thisCourseIncludesData', updated);
                  }}
                  placeholder="Enter item name (e.g., Interactive video lessons)"
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const updated = thisCourseIncludesData.filter((_: any, i: number) => i !== index);
                    updateTypeSpecificField('thisCourseIncludesData', updated);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            <Button
              variant="outline"
              onClick={() => {
                const newItem = { id: Date.now(), name: '' };
                updateTypeSpecificField('thisCourseIncludesData', [...thisCourseIncludesData, newItem]);
              }}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Feature
            </Button>
          </div>
        </Card>

        {/* Program Comparison */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-gray-600" />
              <Label className="text-base font-semibold text-gray-900">Program Comparison</Label>
            </div>
            <p className="text-sm text-gray-500">
              Compare features between your program and competitors for this specific program type
            </p>
            
            {Array.isArray(programComparison) && programComparison.length > 0 ? (
              programComparison.map((item: any, index: number) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Comparison Title</Label>
                        <Input
                          value={item.title || ''}
                          onChange={(e) => {
                            const updated = [...programComparison];
                            updated[index] = { ...item, title: e.target.value };
                            updateTypeSpecificField('programComparison', updated);
                          }}
                          placeholder="e.g., Curriculum Quality"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Note (Optional)</Label>
                        <Input
                          value={item.note || ''}
                          onChange={(e) => {
                            const updated = [...programComparison];
                            updated[index] = { ...item, note: e.target.value };
                            updateTypeSpecificField('programComparison', updated);
                          }}
                          placeholder="Additional notes"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id={`our-program-${index}`}
                          checked={item.ourProgram || false}
                          onCheckedChange={(checked) => {
                            const updated = [...programComparison];
                            updated[index] = { ...item, ourProgram: checked };
                            updateTypeSpecificField('programComparison', updated);
                          }}
                        />
                        <Label htmlFor={`our-program-${index}`} className="text-sm mt-2">Our Program</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id={`other-program-${index}`}
                          checked={item.otherProgram || false}
                          onCheckedChange={(checked) => {
                            const updated = [...programComparison];
                            updated[index] = { ...item, otherProgram: checked };
                            updateTypeSpecificField('programComparison', updated);
                          }}
                        />
                        <Label htmlFor={`other-program-${index}`} className="text-sm mt-2">Other Programs</Label>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const updated = programComparison.filter((_: any, i: number) => i !== index);
                          updateTypeSpecificField('programComparison', updated);
                        }}
                        className="text-red-600 hover:text-red-800 ml-auto"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="text-sm text-muted-foreground p-4 border rounded-lg">
                No comparison items added yet.
              </div>
            )}
            
            <Button
              variant="outline"
              onClick={() => {
                const newItem = {
                  id: `comp-${Date.now()}`,
                  title: '',
                  ourProgram: false,
                  otherProgram: false,
                  note: ''
                };
                updateTypeSpecificField('programComparison', [...(Array.isArray(programComparison) ? programComparison : []), newItem]);
              }}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Comparison Item
            </Button>
          </div>
        </Card>
      </div>

      {/* SECTION 1: Key Features Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Key Features
          </h3>
        </div>

        {/* Common Features Dropdown */}
        {showCommonFeatures && (
          <Card className="p-6 bg-purple-50 border-purple-200">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-600" />
                <Label className="text-sm font-medium text-purple-900">
                  Quick Add Common Features
                </Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {COMMON_KEY_FEATURES.map((commonFeature, index) => {
                  // Check if this common feature is already added
                  const isAlreadyAdded = keyFeatures.some(
                    feature => feature.name === commonFeature.name
                  );
                  
                  return (
                    <Button
                      key={index}
                      onClick={() => addCommonKeyFeature(commonFeature)}
                      disabled={isAlreadyAdded}
                      size="sm"
                      variant="outline"
                      className={`h-auto py-3 px-4 text-left ${
                        isAlreadyAdded 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' 
                          : 'hover:bg-purple-100 border-purple-300 hover:border-purple-400'
                      }`}
                    >
                      <div className="w-full">
                        <div className="font-medium text-sm">{commonFeature.name}</div>
                        <div className="text-xs text-gray-600 mt-1">{commonFeature.value}</div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Existing Key Features */}
        {keyFeatures.map((feature, index) => (
          <Card key={index} className="p-6">
            <div className="space-y-6">
              {/* Top Row: Name, Icon, Value */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Feature Name</Label>
                  <Input
                    placeholder="e.g., Hands-on Practice"
                    value={feature.name}
                    onChange={(e) =>
                      updateKeyFeature(index, {
                        ...feature,
                        name: e.target.value,
                      } as KeyFeature)
                    }
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Icon</Label>
                  <SimpleIconPicker
                    value={feature.icon}
                    onValueChange={(value) =>
                      updateKeyFeature(index, {
                        ...feature,
                        icon: value,
                      } as KeyFeature)
                    }
                    searchPlaceholder="Search for an icon..."
                    triggerPlaceholder="Select an icon"
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Value</Label>
                  <Input
                    placeholder="e.g., 100+ hours"
                    value={feature.value}
                    onChange={(e) =>
                      updateKeyFeature(index, {
                        ...feature,
                        value: e.target.value,
                      } as KeyFeature)
                    }
                    className="w-full"
                  />
                </div>
              </div>

              {/* Description Row */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Description</Label>
                <div className="border border-input rounded-md p-1 min-h-[120px] bg-muted/30">
                  <RichTextEditor
                    value={feature.description}
                    onChange={(value) =>
                      updateKeyFeature(index, {
                        ...feature,
                        description: value,
                      })
                    }
                    minHeight={120}
                    showBorder={true}
                    enableAI={true}
                    title={`Key Feature: ${feature.name || 'Description'}`}
                    aiContext={`Program type: ${activeProgramType}, Feature: ${feature.name}`}
                  />
                </div>
              </div>

              {/* Action Button */}
              <div className="flex justify-end pt-2 border-t border-gray-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeKeyFeature(index)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove Feature
                </Button>
              </div>
            </div>
          </Card>
        ))}

        {/* Add Features Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <Button
            onClick={addKeyFeature}
            size="sm"
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Add Custom Feature
          </Button>
          <Button
            onClick={() => setShowCommonFeatures(!showCommonFeatures)}
            size="sm"
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Sparkles className="h-4 w-4" />
            {showCommonFeatures ? 'Hide' : 'Show'} Common Features
          </Button>
        </div>
      </div>
    </div>
  );
};
